import React from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';
import { ConsolidatedReportModel } from '../consolidatedReportModel';
import { getFindingFilters } from '../fullBodyScanReportUtils';
import {
  ConsolidatedReportHeaderPdf,
  ConsolidatedSystemPdfSection
} from './ConsolidatedReportPdfSections';
import { pdfStyles } from './pdfTheme';

export interface FullBodyScanConsolidatedPdfProps {
  model: ConsolidatedReportModel;
}

type ChipBorderOverride = typeof pdfStyles.chipCritical;
type ChipCountOverride = typeof pdfStyles.chipCountCritical;

/** Border-colour overrides layered on top of `pdfStyles.chip`. */
const CHIP_EXTRA_BY_KEY: Record<string, ChipBorderOverride> = {
  important: pdfStyles.chipCritical,
  critical_finding: pdfStyles.chipCritical,
  incidental: pdfStyles.chipIncidental,
  significant_actionable_finding: pdfStyles.chipSignificant,
  indeterminate_finding: pdfStyles.chipInconclusive,
  seen_not_significant: pdfStyles.chipSeen,
  benign_incidental_finding: pdfStyles.chipBenign
};

/** Background-colour overrides layered on top of `pdfStyles.chipCount`. */
const CHIP_COUNT_EXTRA_BY_KEY: Record<string, ChipCountOverride> = {
  important: pdfStyles.chipCountCritical,
  critical_finding: pdfStyles.chipCountCritical,
  incidental: pdfStyles.chipCountIncidental,
  significant_actionable_finding: pdfStyles.chipCountSignificant,
  indeterminate_finding: pdfStyles.chipCountInconclusive,
  seen_not_significant: pdfStyles.chipCountSeen,
  benign_incidental_finding: pdfStyles.chipCountBenign
};

const FilterChips: React.FC<{
  counts: ConsolidatedReportModel['findingCounts'];
  bookingUsesV2: boolean;
}> = ({ counts, bookingUsesV2 }) => (
  <View style={pdfStyles.chipRow}>
    {getFindingFilters(bookingUsesV2).map((filter) => {
      const chipExtra = CHIP_EXTRA_BY_KEY[filter.key] ?? CHIP_EXTRA_BY_KEY[filter.id];
      const countExtra = CHIP_COUNT_EXTRA_BY_KEY[filter.key] ?? CHIP_COUNT_EXTRA_BY_KEY[filter.id];
      const chipStyle = chipExtra ? [pdfStyles.chip, chipExtra] : pdfStyles.chip;
      const countWrapStyle = countExtra ? [pdfStyles.chipCount, countExtra] : pdfStyles.chipCount;

      return (
        <View key={filter.id} style={chipStyle}>
          <View style={countWrapStyle}>
            <Text style={pdfStyles.chipCountText}>{counts[filter.id] || 0}</Text>
          </View>
          <Text style={pdfStyles.chipLabel}>{filter.label}</Text>
        </View>
      );
    })}
  </View>
);

/** PDF document aligned with the on-screen consolidated report layout. */
const FullBodyScanConsolidatedPdf: React.FC<FullBodyScanConsolidatedPdfProps> = ({ model }) => (
  <Document title="Full Body Scan — Consolidated Report">
    <Page size="A4" style={pdfStyles.page} wrap>
      <ConsolidatedReportHeaderPdf model={model} />

      <View style={pdfStyles.filterBar}>
        <View style={pdfStyles.filterSummaryRow}>
          <Text style={pdfStyles.filterLabel}>Filter</Text>
          <Text style={pdfStyles.filterValue}>{model.filterSummary}</Text>
        </View>
        <FilterChips counts={model.findingCounts} bookingUsesV2={model.bookingUsesV2} />
      </View>

      {model.filteredSystems.length === 0 ? (
        <View style={pdfStyles.systemShell}>
          <Text style={pdfStyles.mutedText}>No findings match the selected filters.</Text>
        </View>
      ) : (
        model.filteredSystems.map((system) => (
          <ConsolidatedSystemPdfSection
            key={system.key}
            system={system}
            bookingUsesV2={model.bookingUsesV2}
          />
        ))
      )}
    </Page>
  </Document>
);

export default FullBodyScanConsolidatedPdf;
